import * as React from 'react';

import { enumZoneStatus } from '@rematter/frontend-lib-api-client';
import { Button, DataGridColumn, Menu, MenuItem } from '@rematter/ui-web';
import {
  LucideArchive,
  LucideEdit,
  LucideMoreHorizontal,
  LucideXCircle,
} from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';
import { InventoryZoneRecord } from '../../types/inventoryZone';

import { ZoneStatusBadge } from './ZoneStatusBadge';
import { ZoneTypeBadge } from './ZoneTypeBadge';

type UseInventoryZonesColumnsArgs = {
  onRowClick?: (cell: { row: InventoryZoneRecord }) => void;
  onEdit?: (zone: InventoryZoneRecord) => void;
  onClose?: (zone: InventoryZoneRecord) => void;
  onArchive?: (zone: InventoryZoneRecord) => void;
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const renderZoneCodeCell = ({ row }: { row: InventoryZoneRecord }) => (
  <div className="flex flex-col">
    <span className="font-medium">{row.zoneCode}</span>
    <span className="text-xs text-neutral/60">{row.name}</span>
  </div>
);

const renderFacilityCell = ({ row }: { row: InventoryZoneRecord }) => (
  <span className="text-sm">{row.facilityName || '—'}</span>
);

const renderParentZoneCell = ({ row }: { row: InventoryZoneRecord }) => (
  <span className="text-sm">{row.parentZoneCode || '—'}</span>
);

const renderZoneTypeCell = ({ row }: { row: InventoryZoneRecord }) => (
  <ZoneTypeBadge zoneType={row.zoneType} />
);

const renderStatusCell = ({ row }: { row: InventoryZoneRecord }) => (
  <ZoneStatusBadge status={row.status} />
);

const renderCapacityCell = ({ row }: { row: InventoryZoneRecord }) => {
  if (row.capacityValue === null) return <span className="text-sm">—</span>;
  return (
    <span className="text-sm tabular-nums">
      {row.capacityValue} {row.capacityUnit || ''}
    </span>
  );
};

const renderCreatedAtCell = ({ row }: { row: InventoryZoneRecord }) => (
  <span className="text-sm">{formatDate(row.createdAt)}</span>
);

export const useInventoryZonesColumns = ({
  onRowClick,
  onEdit,
  onClose,
  onArchive,
}: UseInventoryZonesColumnsArgs = {}): DataGridColumn<InventoryZoneRecord>[] => {
  const t = useTranslations();

  return React.useMemo(() => {
    const columns: DataGridColumn<InventoryZoneRecord>[] = [
      {
        key: 'zoneCode',
        header: t.zonesListView.table.columns.zoneCode,
        onClick: onRowClick,
        cell: renderZoneCodeCell,
        width: 200,
        sortable: true,
      },
      {
        key: 'facility',
        header: t.zonesListView.table.columns.facility,
        onClick: onRowClick,
        cell: renderFacilityCell,
        width: 180,
      },
      {
        key: 'parentZone',
        header: t.zonesListView.table.columns.parentZone,
        onClick: onRowClick,
        cell: renderParentZoneCell,
        width: 150,
      },
      {
        key: 'zoneType',
        header: t.zonesListView.table.columns.zoneType,
        onClick: onRowClick,
        cell: renderZoneTypeCell,
        width: 120,
        sortable: true,
      },
      {
        key: 'status',
        header: t.zonesListView.table.columns.status,
        onClick: onRowClick,
        cell: renderStatusCell,
        width: 100,
      },
      {
        key: 'capacity',
        header: t.zonesListView.table.columns.capacity,
        onClick: onRowClick,
        cell: renderCapacityCell,
        width: 120,
      },
      {
        key: 'createdAt',
        header: t.zonesListView.table.columns.createdAt,
        onClick: onRowClick,
        cell: renderCreatedAtCell,
        width: 130,
        sortable: true,
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => {
          const zone = row as InventoryZoneRecord;
          const canClose = zone.status === enumZoneStatus.Active;
          const canArchive = zone.status === enumZoneStatus.Closed;

          const menuItems: MenuItem[] = [
            {
              label: (
                <div className="flex items-center gap-2">
                  <LucideEdit className="size-4" />
                  {t.zonesListView.actions.edit}
                </div>
              ),
              onAction: () => onEdit?.(zone),
            },
          ];

          if (canClose) {
            menuItems.push('separator');
            menuItems.push({
              label: (
                <div className="flex items-center gap-2">
                  <LucideXCircle className="size-4" />
                  {t.zonesListView.actions.close}
                </div>
              ),
              onAction: () => onClose?.(zone),
            });
          }

          if (canArchive) {
            menuItems.push('separator');
            menuItems.push({
              label: (
                <div className="flex items-center gap-2">
                  <LucideArchive className="size-4" />
                  {t.zonesListView.actions.archive}
                </div>
              ),
              onAction: () => onArchive?.(zone),
            });
          }

          return (
            <div className="flex justify-center">
              <Menu
                trigger={
                  <Button
                    size="icon"
                    icon={<LucideMoreHorizontal />}
                    variant="outline"
                  />
                }
                items={menuItems}
              />
            </div>
          );
        },
      },
    ];

    return columns;
  }, [t, onRowClick, onEdit, onClose, onArchive]);
};
